import React from "react";
// Consumer Detail right card

// Default
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "react-lottie";

// Mui
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import Popover from "@mui/material/Popover";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Svg
import Note from "../../assets/svg/note";
import SimCard from "../../assets/svg/simCard";
import Clock3 from "../../assets/svg/Clock3";
import Home from "../../assets/svg/home";
import Download from "../../assets/svg/download";
import MenuIcon from "../../assets/svg/menu";
import Icon360 from "../../assets/svg/360";

// Theme
import theme from "../../theme";

// Custom
import CustomToast from "../toast/CustomToast";

// Redux
import { setMeterId } from "../../redux/features/MeterSlice";

// Utils
import { fetchGETRequest } from "../../utils/services";
import { formatDate, formatMeterSerialNumber } from "../../utils/helper";

// Animation Icons
import SearchAnimation from "../../assets/lordIcons/wired-outline-1725-exit-sign-hover-pinch.json";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { ToastState } from "../../types";

const ConsumerDetailRightCard = ({
  handleActiveMeterStatus,
  chipValue,
  read,
  toggleStatusChange,
  setConfirmModal,
}: {
  handleActiveMeterStatus: (meterId: string) => void;
  chipValue: string | null;
  read: { date: string; value: string };
  toggleStatusChange: () => void;
  setConfirmModal: (modal: { open: boolean; data: any; type: string }) => void;
}) => {
  const currentTimestamp = Date.now();
  const matches = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { totalMeters, allMeters } = useAppSelector((state) => state.meter);
  const { customerId } = useAppSelector((state) => state.customer);

  const [value, setValue] = useState("Meter 1");
  // const [chip, setChip] = useState("Active");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpenPopover = Boolean(anchorEl);
  const [meterDetail, setMeterDetail] = useState(allMeters[0]);

  const [optoinAnchorEl, setOptionAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const isOpenOption = Boolean(optoinAnchorEl);

  const handleCloseAnchors = () => {
    setAnchorEl(null);
    setOptionAnchorEl(null);
  };

  const [timeStamp, setTimeStamp] = useState({
    start: currentTimestamp - 24 * 60 * 60 * 1000,
    end: currentTimestamp,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });

  const handleChange = (_: unknown, newValue: string) => {
    setValue(newValue);
  };

  const COLUMNS = ["Meter Serial Number", "Reason for Replacement"];

  const ROWS = [
    {
      meterSerialNumber: "MSN-00123",
      reasonForReplacement: "Upgraded to a smart meter",
    },
    { meterSerialNumber: "MSN-00456", reasonForReplacement: "Faulty meter" },
    {
      meterSerialNumber: "MSN-00789",
      reasonForReplacement: "Meter reading inaccuracies",
    },
    { meterSerialNumber: "MSN-01234", reasonForReplacement: "Physical damage" },
    { meterSerialNumber: "MSN-01567", reasonForReplacement: "End of life" },
    {
      meterSerialNumber: "MSN-01987",
      reasonForReplacement: "Customer requested replacement",
    },
    {
      meterSerialNumber: "MSN-02345",
      reasonForReplacement: "Compliance with new regulations",
    },
    {
      meterSerialNumber: "MSN-02890",
      reasonForReplacement: "Upgraded technology",
    },
    {
      meterSerialNumber: "MSN-03321",
      reasonForReplacement: "Change of service provider",
    },
    { meterSerialNumber: "MSN-03876", reasonForReplacement: "System upgrade" },
    {
      meterSerialNumber: "MSN-04567",
      reasonForReplacement: "Network compatibility issues",
    },
    {
      meterSerialNumber: "MSN-05123",
      reasonForReplacement: "Performance enhancement",
    },
    {
      meterSerialNumber: "MSN-05987",
      reasonForReplacement: "Replacement due to theft",
    },
  ];

  const downloadCSV = (data: any, meter: string) => {
    const headers = Object.keys(data[0]).join(",");
    const rows = data
      .map((row: any) => Object.values(row).join(","))
      .join("\n");
    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${window.location.pathname.slice(1)}-${meter}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const bulkDownload = async () => {
    setIsLoading(true);

    const metersResponse = await fetchGETRequest(
      `customer/${customerId}/devices?pageSize=20&page=0`,
      {},
      true
    );

    if (metersResponse?.data?.data?.length > 0) {
      const allMeters = metersResponse?.data?.data?.map((item: any) => {
        return { id: item?.id?.id, name: item?.name };
      });

      for (let i = 0; i < allMeters.length; i++) {
        const meter = allMeters[i]["id"];
        const response = await fetchGETRequest(
          `plugins/telemetry/DEVICE/${meter}/values/timeseries?keys=IP_KWH,IP_MeterTime,IP_Current,IP_Voltage,IP_Frequency,IP_NeutralCurrent,IP_PowerFactor,IP_KW,IP_KVA,IP_KWHEXP,IP_KVAH,IP_KVAHEXP,IP_MDKW,IP_MDKVA,IP_MDResetCount,IP_ProgramCount,IP_TamperCount,IP_TotalPowerOnDuration,IP_RelayStatus,IP_LoadLimitValue,EventCode,EventDescription&startTs=${timeStamp?.start}&endTs=${timeStamp?.end}&intervalType=WEEK`,
          {},
          true
        );

        if (response?.data && Object.keys(response?.data).length !== 0) {
          const keys = Object.keys(response?.data);
          const maxRows = Math.max(
            ...Object.values(response?.data).map((arr: any) => arr.length)
          );

          const tableData = Array.from({ length: maxRows }).map(
            (_, rowIndex) => {
              let rowObject = {
                meter: allMeters[i]["name"],
                timestamp:
                  formatDate(
                    new Date(response?.data[keys[0]][rowIndex]?.ts),
                    "DD/MM/YYYY HH:mm:ss"
                  ) || "N/A",
              };
              keys.forEach((key: string) => {
                rowObject[key.replace("IP_", "") as keyof typeof rowObject] =
                  response?.data[key][rowIndex]?.value || "N/A";
              });
              return rowObject;
            }
          );
          downloadCSV(tableData, meter);
        } else {
          setToast({
            message: `No data found for meter: ${meter}`,
            severity: "error",
          });
        }
      }
    } else {
      setToast({
        message: `No meters found.`,
        severity: "error",
      });
    }

    setIsLoading(false);
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: SearchAnimation,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  return (
    <Card className="consumer_right_card">
      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />
      <Box>
        <TabContext value={value}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "#CACDD7",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Box sx={{ maxWidth: 420 }}>
              <TabList
                onChange={handleChange}
                variant={"scrollable"}
                scrollButtons={true}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: "##1280E7",
                    height: "3px",
                  },
                }}>
                {Array.from({ length: totalMeters }, (_, index) => {
                  return (
                    <Tab
                      key={index}
                      label={
                        <Box display="flex" alignItems="center" gap={"7px"}>
                          Meter {index + 1}
                          <Note />
                        </Box>
                      }
                      value={`Meter ${index + 1}`}
                      className="consumer_right_card_list_tab"
                      onClick={() => {
                        handleActiveMeterStatus(allMeters[index]?.id);
                        setMeterDetail(allMeters[index]);
                        // setChip(
                        //   firstLetterUppercase(allMeters[index]?.meterStatus)
                        // );
                      }}
                      sx={{
                        padding: "0px !important",
                        gap: 0,
                      }}
                    />
                  );
                })}
              </TabList>
            </Box>

            <Stack direction={"row"}>
              <Box
                sx={{
                  width: "1px",
                  height: "31px",
                  background: "#CACDD7",
                  margin: "0px 10px",
                }}></Box>
              <IconButton
                onClick={(event) => {
                  setOptionAnchorEl(event.currentTarget);
                }}
                sx={{ width: 20 }}>
                <MenuIcon />
              </IconButton>
            </Stack>
          </Box>

          <TabPanel value={value} sx={{ padding: 0 }}>
            <Grid container>
              <Grid
                size={{ xs: 4, md: 6, lg: 4 }}
                container
                mt={matches ? "12px" : "18px"}
                mb={!matches ? "28px" : "0px"}
                order={{ md: 1, lg: 1 }}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={"8px"}
                  textAlign={"center"}>
                  <Typography variant="caption" textAlign={"right"}>
                    Meter ID:
                  </Typography>
                  <SimCard />
                  <Typography variant="caption">
                    {meterDetail?.meterId}
                  </Typography>
                </Stack>
              </Grid>

              <Grid
                size={{ xs: 4, md: 6, lg: 3 }}
                container
                justifyContent={matches ? "flex-start" : "center"}
                mt={matches ? "12px" : "18px"}
                mb={!matches ? "28px" : "0px"}
                order={{ md: 3, lg: 2 }}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={"8px"}
                  textAlign={"center"}>
                  <Typography variant="caption" textAlign={"right"}>
                    Sr. No.:
                  </Typography>
                  <SimCard />
                  <Typography variant="caption">
                    {formatMeterSerialNumber(meterDetail?.serialNumber)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid
                size={{ xs: 4, md: 6, lg: 5 }}
                container
                justifyContent={"flex-end"}
                mt={matches ? "12px" : "18px"}
                mb={!matches ? "28px" : "0px"}
                order={{ md: 2, lg: 3 }}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={"8px"}
                  textAlign={"center"}>
                  <Typography variant="caption" textAlign={"right"}>
                    Type:
                  </Typography>
                  <Home />
                  <Stack direction={"row"} gap={"7px"} alignItems={"center"}>
                    <Typography variant="caption">
                      {meterDetail?.meterType}
                    </Typography>

                    <IconButton
                      onClick={(event) =>
                        setAnchorEl(event.currentTarget as HTMLElement)
                      }>
                      <Clock3 fill={"#1280E7"} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Grid>

              <Grid
                size={{ xs: 4, md: 7, lg: 4.5 }}
                container
                mt={matches ? "12px" : "0px"}
                order={{ md: 5, lg: 4 }}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={"8px"}
                  textAlign={"center"}>
                  <Typography variant="caption" textAlign={"right"}>
                    Meter Status:
                  </Typography>
                  <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
                    {(chipValue == "Connect" ||
                      chipValue == "Connect In Progress") && (
                      <Chip
                        label={chipValue}
                        className="consumer_left_card_status_chip consumer_left_card_status_active"
                      />
                    )}
                    {(chipValue == "Disconnect" ||
                      chipValue == "Disconnect In Progress") && (
                      <Chip
                        label={chipValue}
                        className="consumer_left_card_status_chip consumer_left_card_status_disconnected"
                      />
                    )}
                    {chipValue == null && (
                      <Chip
                        label={"Unknown"}
                        className="consumer_left_card_status_chip consumer_left_card_status_undefine"
                      />
                    )}
                  </Stack>
                  {/* {chipValue === "Connect" ||
                    (chipValue === "Connect In Progress" && (
                      <Box
                        onClick={() => {
                          setConfirmModal({
                            open: true,
                            data: meterDetail,
                            type: "disconnect",
                          });
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="#8E8E93"
                          sx={{ cursor: "pointer" }}
                        >
                          Disconnect
                        </Typography>
                      </Box>
                    ))} */}
                </Stack>
              </Grid>

              <Grid
                size={{ xs: 4, md: 6, lg: 4 }}
                container
                mt={matches ? "12px" : "0px"}
                justifyContent={matches ? "flex-end" : "center"}
                order={{ md: 4, lg: 5 }}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={"8px"}
                  textAlign={"center"}>
                  <Typography variant="caption" textAlign={"right"}>
                    Last Reading Date:
                  </Typography>
                  <Typography variant="caption">{read?.date || "-"}</Typography>
                </Stack>
              </Grid>

              <Grid
                size={{ xs: 4, md: 5, lg: 3.5 }}
                container
                mt={matches ? "12px" : "0px"}
                justifyContent={"flex-end"}
                order={{ md: 6, lg: 6 }}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={"8px"}
                  textAlign={"center"}>
                  <Typography variant="caption" textAlign={"right"}>
                    Last Read Value:
                  </Typography>
                  <Typography variant="caption">
                    {read?.value || "-"} {read?.value && "kWh"}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </TabPanel>
        </TabContext>
      </Box>

      <Popover
        open={isOpenPopover}
        anchorEl={anchorEl}
        onClose={handleCloseAnchors}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}>
        <TableContainer sx={{ maxHeight: 240, width: "100%", padding: "20px" }}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                {COLUMNS.map((item, index) => {
                  return (
                    <TableCell sx={{ padding: "10px" }} key={index}>
                      <Typography
                        fontSize={"13px"}
                        fontWeight={600}
                        textAlign={"left"}>
                        {item}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {ROWS.map((item, index) => {
                return (
                  <TableRow sx={{ height: "47px" }} key={index}>
                    <TableCell sx={{ padding: 0 }}>
                      <Typography
                        fontSize={"11px"}
                        fontWeight={400}
                        textAlign={"left"}>
                        {item.meterSerialNumber}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ padding: 0 }}>
                      <Typography
                        fontSize={"11px"}
                        fontWeight={400}
                        textAlign={"left"}>
                        {item.reasonForReplacement}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Popover>

      <Menu
        anchorEl={optoinAnchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={isOpenOption}
        onClose={handleCloseAnchors}>
        <MenuItem
          sx={{ gap: 1 }}
          onClick={() => {
            navigate("/meter-dashboard", {
              state: meterDetail?.id,
            });
            dispatch(setMeterId(meterDetail?.id));
          }}>
          <Icon360 />
          <Typography variant="caption">View in Meter 360</Typography>
        </MenuItem>

        <MenuItem onClick={bulkDownload} sx={{ gap: 1 }}>
          {!isLoading ? (
            <Download />
          ) : (
            <Lottie options={defaultOptions} height={20} width={20} />
          )}
          <Typography variant="caption">
            {!isLoading ? "Bulk Download" : "Downloading..."}
          </Typography>
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default ConsumerDetailRightCard;
